export interface IValidateOrganisationNumberResponse {
    validation: OrgNrValidationStatus
    invoiceAddress: IAddress
    postalAddress: IAddress
    signatory: IContactPerson
    name: string
    invoicingEmail: string
    hasVat: boolean
    vatNumber: string
}

export interface IValidateVatNumberResponse {
    validation: VatNrValidationStatus
}

export enum OrgNrValidationStatus {
    Valid = 'Valid',
    Exists = 'Exists',
    Invalid = 'Invalid',
    OnGoing = 'Ongoing',
}

export enum VatNrValidationStatus {
    Valid = 'Valid',
    Exists = 'Exists',
    Invalid = 'Invalid',
    OnGoing = 'Ongoing',
}

export interface ICustomerCompanyInfoResponse {
    city: string
    companyName: string
    email: string
    organizationNumber: string
    registeredForFTax: boolean
    registeredForVAT: boolean
    vatNumber: string
    streetAddress: string
    zipCode: string
}

export interface IMunicipality {
    code: number
    description: string
}

export interface IHeard {
    id: number
    description: string
}

export interface IAddress {
    street1?: string
    street2?: string
    zipCode?: string
    city?: string
}

export interface IContactPerson {
    firstname?: string
    lastname?: string
    email?: string
    mobileNumber?: string
    phoneNumber?: string
}

export interface ISubmitProducerRegistrationBody {
    /* Step 1 */
    organizationNumber: string
    vatNumber: string
    optionalConnection: boolean
    /* Step 2 */
    CompanyInformation: {
        companyName: string
        registeredForFTax: boolean
        registeredForVAT: boolean
        streetAddress: string
        zipCode: string
        city: string
        country: string
        email: string
        articleSupplierTypeCodes: number[]
    }
    /* Step 3 */
    ProjectedSales: {
        metalPackaging: number | null
        plasticPackagingSmall: number | null
        plasticPackagingLarge: number | null
    }
    /* Step 4 */
    ContactInformation: {
        oneContact: boolean
        terms: boolean
        agreement?: IContactPerson
        signatory?: IContactPerson
        productManager?: IContactPerson
        salesReporting?: IContactPerson
        combined?: IContactPerson
    }
    /* Step 5 */
    GDPRConsentText?: string
}

interface ISubmitRegistrationBody {
    /* Step 1 */
    organizationNumber?: string
    /* Step 2 */
    name?: string
    hasFTax: boolean
    hasVAT: boolean
    vatNumber?: string
    accountType?: string
    accountNumber?: string
    ownerPostalAddress: IAddress
    /* Step 3 */
    customerSiteName?: string
    municipalityCode?: number
    visitingAddress: IAddress
    visitingAddressIsAlsoPostalAddress: boolean
    postalAddress: IAddress
    message?: string
    /* Step 4 */
    contactPerson: IContactPerson
    monthlyReportPerson: IContactPerson
    /* Step 5 */
    GDPRConsentText?: string
}

export interface ISubmitStoreRegistrationBody extends ISubmitRegistrationBody {
    /* Step 2 */
    newOwnerForExistingStore: boolean
    newOwnerForExistingStoreChangeDate?: string
    /* Step 3 */
    hasRVM: boolean
    rvmErrorReportPersonEmail?: string
    unitName?: string
    signatory?: IContactPerson | null
    deliveryAddress?: IAddress
    invoiceAddress?: IAddress
    invoicingEmail?: string
}

export interface ISubmitRestaurantRegistrationBody
    extends ISubmitRegistrationBody {
    /* Step 2 */
    newOwnerForExistingStore: boolean
    newOwnerForExistingStoreChangeDate?: string
    /* Step 3 */
    unitName?: string
}

export interface ISubmitAssociationRegistrationBody {
    /* Step 1 */
    organizationNumber?: string
    /* Step 2 */
    name?: string
    hasFTax: boolean
    hasVAT: boolean
    vatNumber?: string
    accountType?: string
    accountNumber?: string
    ownerPostalAddress: IAddress
    /* Step 3 */
    customerSiteName?: string
    municipalityCode?: number
    depositAlternative: string
    visitingAddress: IAddress
    visitingAddressIsAlsoPostalAddress: boolean
    message?: string
    howTheyHeardOfUsId?: string
    freeCardboardContainer?: boolean
    freeFloorballContainer?: boolean
    postalAddress?: IAddress
    deliveryAddress?: IAddress
    acceptDeliveryWithoutAck?: boolean
    /* Step 4 */
    contactPerson: IContactPerson
    monthlyReportPerson: IContactPerson
    /* Step 5 */
    GDPRConsentText?: string
}

export interface ISubmitCampsiteRegistrationBody {
    /* Step 1 */
    organizationNumber?: string
    /* Step 2 */
    newOwnerForExistingStore: boolean
    newOwnerForExistingStoreChangeDate?: string
    name?: string
    hasFTax: boolean
    hasVAT: boolean
    vatNumber?: string
    donationReceiverName?: string
    donationReceiverOrganizationNumber?: string
    donationReceiverAccountType?: string
    donationReceiverAccountNumber?: string
    accountType?: string
    accountNumber?: string
    ownerPostalAddress: IAddress
    /* Step 3 */
    customerSiteName?: string
    municipalityCode?: number
    visitingAddress: IAddress
    visitingAddressIsAlsoDeliveryAddress: boolean
    postalAddress: IAddress
    deliveryAddress: IAddress
    message?: string
    howTheyHeardOfUsId?: string
    /* Step 4 */
    contactPerson: IContactPerson
    /* Step 5 */
    GDPRConsentText?: string
}

export type ISubmitSkiResortRegistrationBody = ISubmitCampsiteRegistrationBody
export type ISubmitWorkPlaceRegistrationBody = ISubmitCampsiteRegistrationBody
